import { formatSecondsToTimestamp } from "@memorang/helpers/common";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { CustomAppBar } from "../CustomAppbar";
import Image from "../CustomImage";
import BottomActionsRow from "./BottomActionsRow";
import DesktopBottomPlayer from "./DesktopBottomPlayer";
import PlaybackControls from "./PlaybackControls";
import SeekBar from "./SeekBar";
import type {
	PodcastPlayerContentProps,
	PodcastPlayerProps,
	PodcastPlayerSheetContentProps,
	PodcastPlayerViewProps,
} from "./types";

export { default as DesktopBottomPlayer } from "./DesktopBottomPlayer";
export type {
	PodcastPlayerContentProps,
	PodcastPlayerProps,
	PodcastPlayerSheetContentProps,
	PodcastPlayerViewProps,
};

const PodcastPlayerContent = ({
	title,
	author,
	date,
	artworkUri,
	isPlaying,
	positionSeconds,
	durationSeconds,
	playbackSpeed,
	isBookmarked,
	isFirstTrack,
	isLastTrack,
	onPlayPause,
	onSkipPrevious,
	onSkipNext,
	onRewind,
	onForward,
	onSeek,
	onToggleBookmark,
	onChangeSpeed,
}: PodcastPlayerContentProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	return (
		<Box
			flex={1}
			alignItems="center"
			gap={16}
			paddingHorizontal={16}
			paddingBottom={16}
			paddingTop={24}
			maxWidth={560}
			alignSelf="center"
			width="100%"
		>
			<Image
				source={{ uri: artworkUri }}
				accessibilityLabel={t("podcast.a11y.artwork", { title })}
				style={[
					styles.artwork,
					{
						borderRadius: theme.roundness * 3,
						shadowColor: theme.colors.shadow,
					},
				]}
				contentFit="cover"
			/>

			<Box flex={1} justifyContent="space-between" width="100%">
				<Box gap={16} width="100%">
					<Box gap={8} width="100%">
						<Text
							variant="titleMedium"
							numberOfLines={2}
							style={[styles.centeredText, { color: theme.colors.onSurface }]}
						>
							{title}
						</Text>
						{!!author && (
							<Text
								variant="labelMedium"
								style={[
									styles.centeredText,
									{ color: theme.colors.onSurfaceVariant },
								]}
							>
								{author}
							</Text>
						)}
						{!!date && (
							<Text
								variant="bodySmall"
								style={[
									styles.centeredText,
									{ color: theme.colors.onSurfaceVariant },
								]}
							>
								{date}
							</Text>
						)}
					</Box>

					<Box gap={4} width="100%">
						<SeekBar
							variant="full"
							position={positionSeconds}
							duration={durationSeconds}
							onSeek={onSeek}
						/>
						<Box
							flexDirection="row"
							justifyContent="space-between"
							width="100%"
						>
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{formatSecondsToTimestamp(positionSeconds)}
							</Text>
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{formatSecondsToTimestamp(durationSeconds)}
							</Text>
						</Box>
					</Box>

					<PlaybackControls
						isPlaying={isPlaying}
						isFirstTrack={isFirstTrack}
						isLastTrack={isLastTrack}
						onPlayPause={onPlayPause}
						onSkipPrevious={onSkipPrevious}
						onSkipNext={onSkipNext}
						onRewind={onRewind}
						onForward={onForward}
					/>
				</Box>

				<BottomActionsRow
					playbackSpeed={playbackSpeed}
					isBookmarked={isBookmarked}
					onChangeSpeed={onChangeSpeed}
					onToggleBookmark={onToggleBookmark}
				/>
			</Box>
		</Box>
	);
};

export const PodcastPlayerView = ({
	collectionTitle,
	onBackPress,
	...contentProps
}: PodcastPlayerViewProps) => {
	const theme = useAppTheme();

	return (
		<Box flex={1} backgroundColor={theme.colors.background}>
			<CustomAppBar title={collectionTitle} onPressBack={onBackPress} />
			<PodcastPlayerContent {...contentProps} />
		</Box>
	);
};

export const PodcastPlayer = ({
	visible,
	collectionTitle,
	onBackPress,
	...contentProps
}: PodcastPlayerProps) => {
	const { isMobile } = useDevice();

	if (typeof visible === "boolean") {
		if (!visible) {
			return null;
		}
		if (!isMobile) {
			return <DesktopBottomPlayer {...contentProps} />;
		}
		return (
			<PodcastPlayerView
				{...contentProps}
				collectionTitle={collectionTitle}
				onBackPress={onBackPress}
			/>
		);
	}

	return <PodcastPlayerContent {...contentProps} />;
};

const styles = StyleSheet.create({
	artwork: {
		width: "100%",
		maxWidth: 280,
		aspectRatio: 1,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.1,
		shadowRadius: 32,
		elevation: 8,
	},
	centeredText: {
		textAlign: "center",
	},
});
